import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo, useRef, } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/common/Toast/ToastContext";
import { sitesService } from "@/api/services/sites.service";
import { applyMainOtherFromCostParams, } from "@/features/admin/estimating/mainJobOtherUtils";
import { contactsService } from "@/api/services/contacts.service";
import { quotesService, } from "@/api/services/quotes.service";
import { calculateTotalCost, getShopfrontSettingsFromItems, } from "@/utils/costCalculations";
import { searchSuburbsAsync } from "@/data/australian-suburbs";
import { getContactDisplayName } from "@/utils/string";
import { isUuid } from "@/utils/validation";
import { resolveShoppingCentreIdFromContact } from "@/utils/shoppingCentreMatch";
import { SearchableShoppingCentreSelect } from "@/features/admin/contacts/components/SearchableShoppingCentreSelect";
import styles from "./SiteDetailsTab.module.css";
const WORK_DETAILS_CATEGORY_TABS = [
    { id: "compliance", label: "Compliance/Startup" },
    { id: "service_trades", label: "Service Trades" },
    { id: "flooring", label: "Flooring" },
    { id: "internal_demolition", label: "Internal Demolition" },
    { id: "preliminary_cost", label: "Preliminary Cost" },
    { id: "summary", label: "Summary" },
];
// Building type options - single select only
const BUILDING_TYPE_OPTIONS = [
    { value: "", label: "Select Building Type" },
    { value: "COMMERCIAL", label: "Commercial" },
    { value: "RETAIL", label: "Retail" },
    { value: "HOSPITALITY", label: "Hospitality" },
    { value: "INDUSTRIAL", label: "Industrial" },
];
// Mapping from display labels to enum values (for backward compatibility)
const BUILDING_TYPE_LABEL_TO_VALUE = {
    Commercial: "COMMERCIAL",
    Retail: "RETAIL",
    Hospitality: "HOSPITALITY",
    Industrial: "INDUSTRIAL",
};
// Default area information template
const DEFAULT_AREA_INFO = [
    { label: "Tenancy Defit (m2)", value: 0 },
    { label: "Wall Finish (Plaster) (m2)", value: 0 },
    { label: "Wall Finish (Brickwork) (m2)", value: 0 },
    { label: "Shopfront (l/m)", value: 0 },
    { label: "Shopfront (under 3.6m) OR of Kiosk Lm (m2)", value: 0 },
];
// Helper function to merge site areaInfo with default template
const mergeAreaInfo = (siteAreaInfo, defaultTemplate) => {
    return defaultTemplate.map((defaultItem) => {
        const siteItem = siteAreaInfo?.find((item) => item.label === defaultItem.label);
        return {
            ...defaultItem,
            value: siteItem?.value ?? defaultItem.value,
        };
    });
};
// Default accordion structure for each category
const getDefaultAccordionData = () => ({
    compliance: [
        {
            id: "demolition",
            title: "Demolition",
            sections: [
                {
                    sectionId: "hoardings",
                    sectionTitle: "Hoardings (note rates are on m2 basis)",
                    items: [
                        {
                            id: "hoarding-internal-solid",
                            title: "Internal Solid Hoarding",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hoarding-kiosk",
                            title: "Kiosk Hoarding",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "compliance-main",
            title: "Compliance",
            sections: [
                {
                    sectionId: "permits",
                    items: [
                        {
                            id: "comp-demolition-permit",
                            title: "Demolition Permit",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "comp-building-permit",
                            title: "Building Permit",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    service_trades: [
        {
            id: "services-electrical",
            title: "Services - Electrical",
            sections: [
                {
                    sectionId: "electrical",
                    items: [
                        {
                            id: "elec-1",
                            title: "Isolate and Make Safe Tenancy Single EDB and IDF",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "elec-2",
                            title: "Isolate and Make Safe Tenancy Dual EDB and IDF",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "fire",
            title: "Fire",
            sections: [
                {
                    sectionId: "fire",
                    showQty: true,
                    items: [
                        {
                            id: "fire-1",
                            title: "Sprinkler – plugging up to 3m",
                            inScope: false,
                            qty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fire-2",
                            title: "Wet System Drain Down and Recommission - note each means 1 drain down and recommission",
                            inScope: false,
                            qty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fire-3",
                            title: "Smoke Detector – relocation up to 3m",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                        {
                            id: "fire-4",
                            title: "Electronic smoke alarm isolation/de-isolation",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                        {
                            id: "fire-5",
                            title: "EWIS – relocation up to 10m with Fig. 8 cable",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "hydraulics",
            title: "Hydraulics",
            sections: [
                {
                    sectionId: "hydraulics",
                    showQty: true,
                    items: [
                        {
                            id: "hyd-1",
                            title: "Attendance including Cap and Seal Hydraulics Points - 1 water and 1 waste water",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                        {
                            id: "hyd-2",
                            title: "Cap and Seal additional hydraulics points - 1 water and 1 waste water",
                            inScope: false,
                            qty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hyd-3",
                            title: "Penetration of plaster ceiling and backfill",
                            inScope: false,
                            qty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "hvac",
            title: "HVAC - Tenant Supplementary Units",
            sections: [
                {
                    sectionId: "hvac",
                    items: [
                        {
                            id: "hvac-1",
                            title: "Split ducted - In ceiling space air cooled FCU with external condenser",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hvac-2",
                            title: "Split cassette - Ceiling mounted integrated air cooled unit with external condenser",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hvac-3",
                            title: "Wall hung split - Wall mounted air cooled unit with external condenser",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hvac-4",
                            title: "Split ducted - water cooled no or internally located condenser",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "it-wall",
            title: "IT Wall",
            sections: [
                {
                    sectionId: "it-wall",
                    items: [
                        {
                            id: "it-wall-1",
                            title: "Supply and install plasterboard to perimeter IT walls",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-2",
                            title: "Supply and install new IT wall (13mm Plasterboard either side with 92mm Stud)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-3",
                            title: "Relocate EDB (Switchboard)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-4",
                            title: "Supply Paint",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-5",
                            title: "Relocate Hydraulic Services (underground trenching)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-6",
                            title: "Relocate Hydraulic Services (above ground)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    flooring: [
        {
            id: "floor-finishes",
            title: "Floor Finishes",
            sections: [
                {
                    sectionId: "floor-finishes",
                    items: [
                        {
                            id: "ff-1",
                            title: "Remove Ceramic Tiles (Direct Stick)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ff-2",
                            title: "Remove Vinyl (Walk behind)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ff-3",
                            title: "Remove Carpet Tiles",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ff-4",
                            title: "Remove Timber / Parquetry (Direct Stick)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ff-5",
                            title: "After Hours Works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "screeds",
            title: "Screeds",
            sections: [
                {
                    sectionId: "screeds",
                    items: [
                        {
                            id: "screeds-1",
                            title: "0mm - 50mm",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "screeds-2",
                            title: "50mm - 80mm",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "screeds-3",
                            title: "After Hours works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "grinding",
            title: "Grinding",
            sections: [
                {
                    sectionId: "grinding",
                    items: [
                        {
                            id: "grinding-1",
                            title: "Tenancy Floor Grinding",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "grinding-2",
                            title: "After Hours works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    internal_demolition: [
        {
            id: "kiosks",
            title: "Kiosks",
            sections: [
                {
                    sectionId: "kiosks",
                    items: [
                        {
                            id: "kiosks-1",
                            title: "Specialty",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "kiosks-2",
                            title: "Food & Beverage",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "kiosks-3",
                            title: "Make Good of Mall Tiles",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "joinery-fixtures",
            title: "Joinery & Fixtures",
            sections: [
                {
                    sectionId: "joinery",
                    sectionTitle: "Tenancy Area (0-500m2)",
                    items: [
                        {
                            id: "joinery-0-500-low",
                            title: "Low volume of FF&E & Joinery Disposal (General Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-0-500-moderate",
                            title: "Moderate volume of FF&E & Joinery Disposal (Premium Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-0-500-high-food",
                            title: "High volume of FF&E & Joinery Disposal (Food Service incl. Cool Room)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-0-500-high-jewellers",
                            title: "High volume of FF&E & Joinery Disposal (Jewellers)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
                {
                    sectionId: "joinery",
                    sectionTitle: "Tenancy Area (500-1000m2)",
                    items: [
                        {
                            id: "joinery-500-1000-low",
                            title: "Low volume of FF&E & Joinery Disposal (General Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-500-1000-moderate",
                            title: "Moderate volume of FF&E & Joinery Disposal (Premium Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-500-1000-high-food",
                            title: "High volume of FF&E & Joinery Disposal (Food Service incl. Cool Room)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-500-1000-high-jewellers",
                            title: "High volume of FF&E & Joinery Disposal (Jewellers)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
                {
                    sectionId: "joinery",
                    sectionTitle: "Tenancy Area (1000m2+)",
                    items: [
                        {
                            id: "joinery-1000-plus-low",
                            title: "Low volume of FF&E & Joinery Disposal (General Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-1000-plus-moderate",
                            title: "Moderate volume of FF&E & Joinery Disposal (Premium Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-1000-plus-high-food",
                            title: "High volume of FF&E & Joinery Disposal (Food Service incl. Cool Room)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-1000-plus-high-jewellers",
                            title: "High volume of FF&E & Joinery Disposal (Jewellers)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "demolish-walls",
            title: "Demolish Walls",
            sections: [
                {
                    sectionId: "walls",
                    items: [
                        {
                            id: "walls-1",
                            title: "Remove Blockwork Walls (Single Skin)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "walls-2",
                            title: "Remove Stud IT Walls",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "walls-3",
                            title: "After Hours works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "demolish-shopfronts",
            title: "Demolish Shopfronts",
            sections: [
                {
                    sectionId: "shopfronts",
                    items: [
                        {
                            id: "shopfront-1",
                            title: "Standard glazed & MDF",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-2",
                            title: "Heavy Duty Steel & Stone",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-3",
                            title: "Shopfront Height Factor Std",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-4",
                            title: "Shopfront Height Factor Hvy",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-3a",
                            title: "Shopfront is OVER 3.6m in height",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-5",
                            title: "After Hours Works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "ceiling-type",
            title: "Ceiling Type",
            sections: [
                {
                    sectionId: "ceiling",
                    sectionTitle: "Ceiling Type - to include demolition of mechanical diffusers",
                    items: [
                        {
                            id: "ceiling-1",
                            title: "Open Plan Ceiling",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ceiling-2",
                            title: "Remove Ceiling Tiles & Grid",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ceiling-3",
                            title: "Remove Plaster Lining & Gridwork",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ceiling-4",
                            title: "After Hours Works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    preliminary_cost: [
        {
            id: "prelims-supervision",
            title: "Prelims & Supervision",
            sections: [
                {
                    sectionId: "prelims",
                    items: [
                        {
                            id: "prelims-1",
                            title: "Prelims & Supervision",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "scissor-lift",
            title: "Scissor Lift Hire",
            sections: [
                {
                    sectionId: "scissor-lift",
                    showWeeksQty: true,
                    items: [
                        {
                            id: "scissor-1",
                            title: '"19" Electric',
                            inScope: false,
                            weeksQty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "skip-hire",
            title: "Skip Hire",
            sections: [
                {
                    sectionId: "skip-hire",
                    showNumberQty: true,
                    items: [
                        {
                            id: "skip-1",
                            title: "12m3",
                            inScope: false,
                            numberQty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "skip-2",
                            title: "15m3",
                            inScope: false,
                            numberQty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "skip-3",
                            title: "23m3",
                            inScope: false,
                            numberQty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "other-fees",
            title: "Other Fees Price on Application",
            sections: [
                {
                    sectionId: "other-fees",
                    items: [
                        {
                            id: "fees-1",
                            title: "Specialist consultant - Electrical",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fees-2",
                            title: "Specialist consultant - Hydraulic",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fees-3",
                            title: "Specialist consultant - Structural",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fees-4",
                            title: "Protection works",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fees-5",
                            title: "Specialist Cleaning",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    summary: [
        {
            id: "summary-totals",
            title: "Summary",
            isSummarySection: true,
            summaryData: {
                subTotal: "$0",
                buildersMarginEnabled: true,
                buildersMarginPercentage: "10%",
                buildersMarginAmount: "$0",
                total: "$0",
                costPerM2: "$0",
            },
        },
    ],
});
// Helper to parse currency string to number
const parseCurrency = (value) => {
    if (!value)
        return 0;
    const cleaned = value.replace(/[^0-9.-]/g, "");
    return parseFloat(cleaned) || 0;
};
// Helper to format unit cost for display (decimal without $)
// Helper to check if an item is an After Hours item
const isAfterHoursItem = (itemTitle) => itemTitle.toLowerCase().includes("after hours");
// Helper to calculate sum of Standard glazed & MDF and Heavy Duty Steel & Stone totals
const getShopfrontItemsTotal = (items, shopfrontSettings) => {
    let total = 0;
    items.forEach((item) => {
        const isStandardGlazed = item.id === "shopfront-1" ||
            item.title.toLowerCase().includes("standard glazed") ||
            item.title.toLowerCase().includes("standard");
        const isHeavyDuty = item.id === "shopfront-2" ||
            item.title.toLowerCase().includes("heavy duty") ||
            item.title.toLowerCase().includes("heavy");
        if ((isStandardGlazed || isHeavyDuty) && item.inScope && item.totalCost) {
            let itemTotal = parseCurrency(item.totalCost);
            // Apply height factor if toggle is ON
            if (shopfrontSettings?.heightEnabled) {
                if (isStandardGlazed) {
                    itemTotal *= shopfrontSettings.stdHeightFactor;
                }
                else if (isHeavyDuty) {
                    itemTotal *= shopfrontSettings.hvyHeightFactor;
                }
            }
            total += itemTotal;
        }
    });
    return total;
};
// Helper function to recalculate all items in a section (handles After Hours correctly)
const recalculateSectionItems = (items, sectionId, category, costParameters, areaInfo, preserveScissorLiftTotalCost = false) => {
    // Check After Hours status directly from items array (not from prevData which has stale state)
    const afterHoursItem = items.find((item) => item.title.toLowerCase().includes("after hours"));
    const afterHoursEnabled = afterHoursItem?.inScope || false;
    // Extract After Hours multiplier from the item's unitCost (defaults to 1.3 if not set)
    const afterHoursMultiplier = afterHoursEnabled && afterHoursItem?.unitCost
        ? parseFloat(afterHoursItem.unitCost.toString().replace(/[^0-9.-]/g, "")) || 1.3
        : undefined;
    // Get shopfront settings from current items array (not from prevData which has stale state).
    // This ensures we use the current toggle state, not the previous state and centralizes
    // the logic with the shared helpers in costCalculations.ts.
    const shopfrontSettings = sectionId === "shopfronts"
        ? getShopfrontSettingsFromItems(items, costParameters)
        : {
            heightEnabled: false,
            stdHeightFactor: 1,
            hvyHeightFactor: 1,
        };
    const updatedItems = [...items];
    // Find Float value from Cost Settings (costParameters) for "19" Electric calculation
    const isScissorLiftSection = sectionId === "scissor-lift";
    const floatCostParam = isScissorLiftSection
        ? costParameters.find((cp) => cp.category === "preliminary_cost" &&
            cp.sectionId === "scissor-lift" &&
            cp.itemTitle?.toLowerCase().includes("float"))
        : null;
    const floatValue = floatCostParam?.unitCost ?? undefined;
    // For shopfronts section, process shopfront-1 and shopfront-2 first to ensure they're recalculated
    // before the OVER 3.6m item (which depends on their totals)
    if (sectionId === "shopfronts") {
        updatedItems.forEach((subItem, subItemIndex) => {
            const isStandardGlazed = subItem.id === "shopfront-1" ||
                subItem.title.toLowerCase().includes("standard glazed") ||
                subItem.title.toLowerCase().includes("standard");
            const isHeavyDuty = subItem.id === "shopfront-2" ||
                subItem.title.toLowerCase().includes("heavy duty") ||
                subItem.title.toLowerCase().includes("heavy");
            // Recalculate shopfront-1 and shopfront-2 if they're inScope and have unitCost
            if ((isStandardGlazed || isHeavyDuty) &&
                subItem.inScope &&
                subItem.unitCost) {
                const unitCost = parseCurrency(subItem.unitCost);
                if (unitCost > 0) {
                    // Calculate base total cost (without height factor)
                    const calculationResult = calculateTotalCost({
                        itemId: subItem.id,
                        sectionId,
                        category,
                        title: subItem.title,
                        unitCost,
                        quantity: subItem.qty ? parseFloat(subItem.qty) : undefined,
                        areaInfo,
                        afterHoursEnabled,
                        afterHoursMultiplier,
                    });
                    // Apply height factor only if toggle is ON
                    let finalTotal = calculationResult.totalCost;
                    if (shopfrontSettings.heightEnabled) {
                        if (isStandardGlazed) {
                            finalTotal *= shopfrontSettings.stdHeightFactor;
                        }
                        else if (isHeavyDuty) {
                            finalTotal *= shopfrontSettings.hvyHeightFactor;
                        }
                    }
                    updatedItems[subItemIndex] = {
                        ...updatedItems[subItemIndex],
                        totalCost: finalTotal.toFixed(2),
                    };
                }
            }
        });
    }
    // First pass: Calculate all non-After-Hours items
    updatedItems.forEach((subItem, subItemIndex) => {
        if (subItem.inScope && !isAfterHoursItem(subItem.title)) {
            const isShopfrontOver36m = subItem.title
                .toLowerCase()
                .includes("over 3.6m");
            // For OVER 3.6m item, calculate even without unit cost
            if (isShopfrontOver36m && sectionId === "shopfronts") {
                const shopfrontItemsTotal = getShopfrontItemsTotal(updatedItems, shopfrontSettings);
                const calculationResult = calculateTotalCost({
                    itemId: subItem.id,
                    sectionId,
                    category,
                    title: subItem.title,
                    unitCost: 0, // Not used for OVER 3.6m
                    areaInfo,
                    afterHoursEnabled,
                    afterHoursMultiplier,
                    shopfrontItemsTotal,
                });
                updatedItems[subItemIndex] = {
                    ...updatedItems[subItemIndex],
                    totalCost: calculationResult.totalCost.toFixed(2),
                };
            }
            else if (subItem.unitCost) {
                // For other items, require unit cost
                const unitCost = parseCurrency(subItem.unitCost);
                if (unitCost > 0) {
                    // Skip shopfront-1 and shopfront-2 if we already processed them above
                    const isStandardGlazed = subItem.id === "shopfront-1" ||
                        subItem.title.toLowerCase().includes("standard glazed") ||
                        subItem.title.toLowerCase().includes("standard");
                    const isHeavyDuty = subItem.id === "shopfront-2" ||
                        subItem.title.toLowerCase().includes("heavy duty") ||
                        subItem.title.toLowerCase().includes("heavy");
                    // If this is shopfront-1 or shopfront-2 and we're in shopfronts section, skip (already processed)
                    if (sectionId === "shopfronts" && (isStandardGlazed || isHeavyDuty)) {
                        // Already processed above, skip
                    }
                    else {
                        // For scissor-lift section, preserve existing totalCost if preserveScissorLiftTotalCost is true
                        // This prevents overwriting API values during recalculation (e.g., from areaInfo changes)
                        // The totalCost will be recalculated when user explicitly changes unitCost or weeksQty
                        const hasTotalCost = subItem.totalCost &&
                            typeof subItem.totalCost === "string" &&
                            subItem.totalCost.trim() !== "";
                        const shouldPreserveTotalCost = preserveScissorLiftTotalCost &&
                            isScissorLiftSection &&
                            hasTotalCost;
                        if (shouldPreserveTotalCost && subItem.totalCost) {
                            const totalCostStr = typeof subItem.totalCost === "string"
                                ? subItem.totalCost
                                : String(subItem.totalCost);
                            const existingTotalCost = parseFloat(totalCostStr.replace(/[^0-9.-]/g, ""));
                            // If totalCost exists and is valid (> 0), preserve it to avoid overwriting API values
                            // Only recalculate if totalCost is missing or invalid
                            if (!isNaN(existingTotalCost) && existingTotalCost > 0) {
                                // Preserve existing totalCost, skip recalculation
                                // Don't update this item's totalCost
                            }
                            else {
                                // totalCost exists but is invalid, proceed with recalculation
                                const is19Electric = isScissorLiftSection &&
                                    subItem.title.toLowerCase().includes("19") &&
                                    subItem.title.toLowerCase().includes("electric");
                                const calculationResult = calculateTotalCost({
                                    itemId: subItem.id,
                                    sectionId,
                                    category,
                                    title: subItem.title,
                                    unitCost,
                                    quantity: subItem.qty ? parseFloat(subItem.qty) : undefined,
                                    weeksQty: subItem.weeksQty
                                        ? parseFloat(subItem.weeksQty)
                                        : undefined,
                                    numberQty: subItem.numberQty
                                        ? parseFloat(subItem.numberQty)
                                        : undefined,
                                    areaInfo,
                                    afterHoursEnabled,
                                    afterHoursMultiplier,
                                    floatValue: is19Electric ? floatValue : undefined,
                                });
                                updatedItems[subItemIndex] = {
                                    ...updatedItems[subItemIndex],
                                    totalCost: calculationResult.totalCost.toFixed(2),
                                };
                            }
                        }
                        else {
                            // Not scissor-lift or no existing totalCost, proceed with normal recalculation
                            // Check if this is "19" Electric in scissor-lift section
                            const is19Electric = isScissorLiftSection &&
                                subItem.title.toLowerCase().includes("19") &&
                                subItem.title.toLowerCase().includes("electric");
                            // Calculate base total cost (without height factor)
                            const calculationResult = calculateTotalCost({
                                itemId: subItem.id,
                                sectionId,
                                category,
                                title: subItem.title,
                                unitCost,
                                quantity: subItem.qty ? parseFloat(subItem.qty) : undefined,
                                weeksQty: subItem.weeksQty
                                    ? parseFloat(subItem.weeksQty)
                                    : undefined,
                                numberQty: subItem.numberQty
                                    ? parseFloat(subItem.numberQty)
                                    : undefined,
                                areaInfo,
                                afterHoursEnabled,
                                afterHoursMultiplier,
                                floatValue: is19Electric ? floatValue : undefined,
                            });
                            // Apply height factor for shopfront items if toggle is ON
                            let finalTotal = calculationResult.totalCost;
                            if (sectionId === "shopfronts" &&
                                shopfrontSettings.heightEnabled) {
                                if (isStandardGlazed) {
                                    finalTotal *= shopfrontSettings.stdHeightFactor;
                                }
                                else if (isHeavyDuty) {
                                    finalTotal *= shopfrontSettings.hvyHeightFactor;
                                }
                            }
                            updatedItems[subItemIndex] = {
                                ...updatedItems[subItemIndex],
                                totalCost: finalTotal.toFixed(2),
                            };
                        }
                    }
                }
            }
        }
        else if (!subItem.inScope) {
            // Clear total cost when not in scope (but keep for After Hours items)
            if (!isAfterHoursItem(subItem.title)) {
                updatedItems[subItemIndex] = {
                    ...updatedItems[subItemIndex],
                    totalCost: "",
                };
            }
        }
    });
    // Second pass: Handle toggle-only items and shopfront-3/4 - they should NOT have a totalCost
    updatedItems.forEach((subItem, subItemIndex) => {
        const isAfterHoursToggle = isAfterHoursItem(subItem.title);
        const isShopfrontHeightToggle = sectionId === "shopfronts" &&
            (subItem.id === "shopfront-3a" ||
                subItem.title.toLowerCase().includes("over 3.6m"));
        const isShopfront3Or4 = subItem.id === "shopfront-3" || subItem.id === "shopfront-4";
        // Toggle-only items (After Hours, Shopfront OVER 3.6m) and shopfront-3/4 should NOT have a totalCost
        if (isAfterHoursToggle || isShopfrontHeightToggle || isShopfront3Or4) {
            updatedItems[subItemIndex] = {
                ...updatedItems[subItemIndex],
                totalCost: "", // Empty string instead of calculating
            };
        }
    });
    // Third pass: Clear totalCost for items that are NOT inScope
    // This ensures that when items are toggled to "No", their totalCost is cleared
    // and stays cleared even if recalculation is triggered
    updatedItems.forEach((subItem, subItemIndex) => {
        const isAfterHoursToggle = isAfterHoursItem(subItem.title);
        const isShopfrontHeightToggle = sectionId === "shopfronts" &&
            (subItem.id === "shopfront-3a" ||
                subItem.title.toLowerCase().includes("over 3.6m"));
        const isShopfront3Or4 = subItem.id === "shopfront-3" || subItem.id === "shopfront-4";
        // Skip toggle-only items (already handled in second pass)
        // For all other items, clear totalCost if not inScope
        if (!isAfterHoursToggle && !isShopfrontHeightToggle && !isShopfront3Or4) {
            if (!subItem.inScope) {
                updatedItems[subItemIndex] = {
                    ...updatedItems[subItemIndex],
                    totalCost: "", // Clear totalCost for items not in scope
                };
            }
        }
    });
    return updatedItems;
};
// Helper to format number as currency
const formatCurrency = (value) => {
    return `$${value.toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};
export const SiteDetailsTab = ({ site, onSave, saveHandlerRef, createEstimateHandlerRef, onSummaryTabChange, versionWorkItems, isViewingVersion = false, onSiteCreated, }) => {
    const contactDropdownRef = useRef(null);
    const contactPersonDropdownRef = useRef(null);
    const suburbDropdownRef = useRef(null);
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const { error: showErrorToast, success: showSuccessToast } = useToast();
    const canEditPrices = isAdmin; // Only admins can edit prices
    const [activeWorkCategory, setActiveWorkCategory] = useState("compliance");
    const [expandedAccordions, setExpandedAccordions] = useState(new Set());
    const [accordionData, setAccordionData] = useState(getDefaultAccordionData);
    const [buildersMarginEnabled, setBuildersMarginEnabled] = useState(true);
    const [buildersMarginPercentage] = useState(10);
    const [costParameters, setCostParameters] = useState([]);
    const [refreshingPrices, setRefreshingPrices] = useState(false);
    // Mirror of costParameters readable inside mergeWorkItems without re-creating
    // the callback. Used to union Main Job "Other" rows when the saved-workItems
    // merge runs after cost params (mergeWorkItems' trigger useEffect runs after
    // the union effect and otherwise wipes the union).
    const costParametersRef = useRef([]);
    useEffect(() => {
        costParametersRef.current = costParameters;
    }, [costParameters]);
    // Contact person state
    const [contactPersons, setContactPersons] = useState([]);
    const [loadingContactPersons, setLoadingContactPersons] = useState(false);
    const [contactPersonSearchTerm, setContactPersonSearchTerm] = useState("");
    const [showContactPersonDropdown, setShowContactPersonDropdown] = useState(false);
    const [selectedContactPersonId, setSelectedContactPersonId] = useState("");
    const [selectedContactPersonName, setSelectedContactPersonName] = useState("");
    // Company name state
    const [companyNames, setCompanyNames] = useState([]);
    const [loadingCompanyNames, setLoadingCompanyNames] = useState(false);
    const [companySearchTerm, setCompanySearchTerm] = useState("");
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [selectedCompanyName, setSelectedCompanyName] = useState("");
    // Suburb dropdown state
    const [suburbSearchTerm, setSuburbSearchTerm] = useState("");
    const [showSuburbDropdown, setShowSuburbDropdown] = useState(false);
    const [filteredSuburbs, setFilteredSuburbs] = useState([]);
    const [shoppingCentreId, setShoppingCentreId] = useState("");
    // Editable site info state
    const [siteInfo, setSiteInfo] = useState({
        siteName: "",
        tenancyNumber: "",
        tenancyName: "",
        buildingType: "",
        landlord: "",
        address: "",
        suburb: "",
        state: "",
        postcode: "",
    });
    // Area information state
    const [areaInfo, setAreaInfo] = useState(DEFAULT_AREA_INFO);
    // Initialize site info from props
    useEffect(() => {
        // Take the first building type from the array (single select)
        let buildingType = site.buildingTypes && site.buildingTypes.length > 0
            ? site.buildingTypes[0]
            : "";
        // Map display labels back to enum values for backward compatibility
        if (buildingType && BUILDING_TYPE_LABEL_TO_VALUE[buildingType]) {
            buildingType = BUILDING_TYPE_LABEL_TO_VALUE[buildingType];
        }
        setSiteInfo({
            siteName: site.siteName || "",
            tenancyNumber: site.tenancyNumber || "",
            tenancyName: site.tenancyName || "",
            buildingType: buildingType,
            landlord: site.landlord || "",
            address: site.address || "",
            suburb: site.suburb || "",
            state: site.state || "",
            postcode: site.postcode || "",
        });
        // Set company name state first (with fallback from contactPerson)
        // Company must be set before Contact Person so the dropdown is enabled
        const companyFromSite = site.companyName || site.contactPerson?.companyName;
        if (companyFromSite) {
            setSelectedCompanyName(companyFromSite);
            setCompanySearchTerm(companyFromSite);
        }
        else {
            setSelectedCompanyName("");
            setCompanySearchTerm("");
        }
        // Set contact person state
        if (site.contactPerson) {
            const contactDisplay = getContactDisplayName(site.contactPerson) || site.contactPerson.email;
            const contactIdForApi = site.contactPerson.id ?? site.contactPerson.contactId;
            setSelectedContactPersonId(contactIdForApi);
            setSelectedContactPersonName(contactDisplay);
            setContactPersonSearchTerm(contactDisplay);
        }
        else {
            setSelectedContactPersonId("");
            setSelectedContactPersonName("");
            setContactPersonSearchTerm("");
        }
        // Initialize area info by merging site data with default template
        // This ensures all items are always shown, even with 0 values
        setAreaInfo(mergeAreaInfo(site.areaInfo, DEFAULT_AREA_INFO));
        const centreId = site.shoppingCentreId ??
            site.shoppingCentre?.id ??
            (isUuid(site.shoppingCenter ?? "") ? site.shoppingCenter : "") ??
            "";
        setShoppingCentreId(centreId || "");
    }, [site]);
    // When site has no shoppingCentreId, prefill from contact CRM shoppingCenter name match
    useEffect(() => {
        const centreFromSite = site.shoppingCentreId ??
            site.shoppingCentre?.id ??
            (isUuid(site.shoppingCenter ?? "") ? site.shoppingCenter : "") ??
            "";
        if (centreFromSite)
            return;
        const contactId = (selectedContactPersonId ||
            site.contactPerson?.id) ??
            site.contactPerson?.contactId ??
            "";
        if (!contactId)
            return;
        let cancelled = false;
        (async () => {
            const resolved = await resolveShoppingCentreIdFromContact(contactId);
            if (cancelled || !resolved)
                return;
            setShoppingCentreId((prev) => prev || resolved);
        })();
        return () => {
            cancelled = true;
        };
    }, [site, selectedContactPersonId]);
    // Recalculate area-based items when area info changes
    useEffect(() => {
        setAccordionData((prevData) => {
            const newData = { ...prevData };
            // Iterate through all categories and sections to recalculate area-based items
            Object.keys(newData).forEach((categoryKey) => {
                const category = categoryKey;
                if (category === "summary")
                    return;
                const categoryItems = [...newData[category]];
                categoryItems.forEach((item, itemIndex) => {
                    if (item.sections) {
                        const sections = [...item.sections];
                        sections.forEach((section, sectionIndex) => {
                            const items = [...section.items];
                            // Use recalculateSectionItems helper for two-pass calculation
                            // Preserve scissor-lift totalCost to avoid overwriting API values during areaInfo changes
                            const recalculatedItems = recalculateSectionItems(items, section.sectionId, category, costParameters, areaInfo, true);
                            // Check if any items changed
                            const hasChanges = recalculatedItems.some((item, index) => item.totalCost !== items[index]?.totalCost);
                            if (hasChanges) {
                                sections[sectionIndex] = {
                                    ...section,
                                    items: recalculatedItems,
                                };
                            }
                        });
                        if (sections.some((s) => s.items.some((i) => i.totalCost))) {
                            categoryItems[itemIndex] = { ...item, sections };
                        }
                    }
                });
                newData[category] = categoryItems;
            });
            return newData;
        });
    }, [areaInfo]);
    // Calculate summary values from all work items
    const calculatedSummary = useMemo(() => {
        let subTotal = 0;
        // Iterate through all categories except "summary"
        const categories = [
            "compliance",
            "service_trades",
            "flooring",
            "internal_demolition",
            "preliminary_cost",
        ];
        categories.forEach((category) => {
            const items = accordionData[category];
            items.forEach((accordionItem) => {
                if (accordionItem.sections) {
                    accordionItem.sections.forEach((section) => {
                        section.items.forEach((subItem) => {
                            // Exclude "After Hours Works Required" items from summary total
                            const isAfterHoursItem = subItem.title
                                ?.toLowerCase()
                                .includes("after hours");
                            if (subItem.inScope && subItem.totalCost && !isAfterHoursItem) {
                                subTotal += parseCurrency(subItem.totalCost);
                            }
                        });
                    });
                }
            });
        });
        const marginAmount = buildersMarginEnabled
            ? (subTotal * buildersMarginPercentage) / 100
            : 0;
        const total = subTotal + marginAmount;
        // Use local areaInfo state for live updates
        const tenancyItem = areaInfo.find((item) => item.label.toLowerCase().includes("tenancy") ||
            item.label.toLowerCase().includes("defit"));
        const siteArea = tenancyItem?.value || 0;
        const costPerM2 = siteArea > 0 ? total / siteArea : 0;
        return {
            subTotal: formatCurrency(subTotal),
            buildersMarginAmount: formatCurrency(marginAmount),
            total: formatCurrency(total),
            costPerM2: formatCurrency(costPerM2),
        };
    }, [
        accordionData,
        buildersMarginEnabled,
        buildersMarginPercentage,
        areaInfo,
    ]);
    // Merge work items from API into accordion structure
    const mergeWorkItems = useCallback((workItems) => {
        const newData = getDefaultAccordionData();
        // Seed the Main Job "Other" rows (group2–group5) from the saved work
        // items so the generic loop below can apply their scoping by id.
        applyMainOtherFromCostParams(newData, workItems);
        workItems.forEach((item) => {
            const category = item.category;
            if (!newData[category])
                return;
            const accordionItem = newData[category].find((acc) => acc.sections?.some((s) => s.sectionId === item.sectionId));
            if (accordionItem?.sections) {
                // Search through all sections to find the item by itemId
                // (multiple sections can have the same sectionId, e.g., joinery)
                for (const section of accordionItem.sections) {
                    const subItem = section.items.find((i) => i.id === item.itemId);
                    if (subItem) {
                        subItem.inScope = item.inScope;
                        // Store defaultUnitCost for later use (e.g., when toggling After Hours items)
                        subItem.defaultUnitCost = item.defaultUnitCost ?? null;
                        // Use site unitCost if set, otherwise use defaultUnitCost from cost parameters
                        const unitCostValue = item.unitCost != null
                            ? item.unitCost
                            : (item.defaultUnitCost ?? null);
                        subItem.unitCost = unitCostValue != null ? `${unitCostValue}` : "";
                        // If item is not in scope, always clear totalCost regardless of API value
                        // This ensures that when toggled to "No", the display correctly shows empty
                        subItem.totalCost = !item.inScope
                            ? ""
                            : item.totalCost != null
                                ? `${item.totalCost}`
                                : "";
                        subItem.notes = item.notes || "";
                        // Map quantity to the appropriate field based on sectionId
                        // Check for null/undefined explicitly to allow 0 values
                        if (item.quantity != null) {
                            if (section.sectionId === "scissor-lift") {
                                subItem.weeksQty = item.quantity.toString();
                            }
                            else if (section.sectionId === "skip-hire") {
                                subItem.numberQty = item.quantity.toString();
                            }
                            else {
                                subItem.qty = item.quantity.toString();
                            }
                        }
                        if (item.selectedOption) {
                            subItem.selectedOption = item.selectedOption;
                        }
                        break; // Found the item, no need to continue searching
                    }
                }
            }
        });
        // Union Main Job "Other" rows from current Cost Settings on top of the
        // workItems-seeded accordion, so newly-added Other rows surface here even
        // when this runs after the cost-params load.
        applyMainOtherFromCostParams(newData, costParametersRef.current);
        setAccordionData(newData);
    }, []);
    // Fetch cost parameters
    const fetchCostParameters = useCallback(async () => {
        try {
            const response = await sitesService.getDefaultWorkItems();
            setCostParameters(response.data);
        }
        catch (err) {
            console.error("Error fetching cost parameters:", err);
        }
    }, []);
    useEffect(() => {
        fetchCostParameters();
    }, [fetchCostParameters]);
    // Surface Main Job "Other" rows added in Cost Settings after this site was
    // saved. Compares current default-work-items against the accordion built
    // from saved workItems and unions in any missing rows (inScope:false), while
    // preserving estimator state already applied for existing rows.
    // Depends on site.workItems/version state so it re-runs after mergeWorkItems
    // (which rebuilds the accordion from fresh defaults + saved workItems) has
    // settled — otherwise a cost-params-first / workItems-second load order
    // would wipe the union.
    useEffect(() => {
        if (costParameters.length === 0)
            return;
        setAccordionData((prevData) => {
            const newData = JSON.parse(JSON.stringify(prevData));
            applyMainOtherFromCostParams(newData, costParameters);
            return newData;
        });
    }, [
        costParameters,
        site.workItems,
        versionWorkItems,
        isViewingVersion,
    ]);
    const handleRefreshPrices = useCallback(async () => {
        setRefreshingPrices(true);
        try {
            const response = await sitesService.getDefaultWorkItems();
            const freshCostParams = response.data;
            setCostParameters(freshCostParams);
            setAccordionData((prevData) => {
                const newData = JSON.parse(JSON.stringify(prevData));
                freshCostParams.forEach((cp) => {
                    const category = cp.category;
                    if (!newData[category])
                        return;
                    const accordionItem = newData[category].find((item) => item.sections?.some((section) => {
                        if (section.sectionId !== cp.sectionId)
                            return false;
                        if (cp.sectionId === "joinery" &&
                            cp.sectionTitle &&
                            section.sectionTitle !== cp.sectionTitle) {
                            return false;
                        }
                        return true;
                    }));
                    if (accordionItem?.sections) {
                        for (const section of accordionItem.sections) {
                            const sectionMatches = section.sectionId === cp.sectionId &&
                                (cp.sectionId !== "joinery" ||
                                    !cp.sectionTitle ||
                                    section.sectionTitle === cp.sectionTitle);
                            if (!sectionMatches)
                                continue;
                            const subItem = section.items.find((i) => i.id === cp.itemId);
                            if (subItem) {
                                subItem.defaultUnitCost =
                                    cp.defaultUnitCost ?? cp.unitCost ?? null;
                                subItem.unitCost =
                                    cp.unitCost != null ? `${cp.unitCost}` : subItem.unitCost;
                                break;
                            }
                        }
                    }
                });
                // Refresh the Main Job "Other" rows (group2–group5) from cost
                // settings, carrying over estimator scoping/qty for surviving rows.
                applyMainOtherFromCostParams(newData, freshCostParams);
                const categories = [
                    "compliance",
                    "service_trades",
                    "flooring",
                    "internal_demolition",
                    "preliminary_cost",
                ];
                categories.forEach((category) => {
                    if (category === "summary")
                        return;
                    const categoryItems = newData[category] || [];
                    categoryItems.forEach((item) => {
                        if (item.sections) {
                            item.sections.forEach((section) => {
                                const recalculated = recalculateSectionItems(section.items, section.sectionId, category, freshCostParams, areaInfo, true);
                                section.items = recalculated;
                            });
                        }
                    });
                });
                return newData;
            });
            showSuccessToast("Prices refreshed. Click Save to persist changes.");
        }
        catch (err) {
            console.error("Error refreshing prices:", err);
            showErrorToast("Failed to refresh prices.");
        }
        finally {
            setRefreshingPrices(false);
        }
    }, [areaInfo, showSuccessToast, showErrorToast]);
    useEffect(() => {
        // Use version work items if viewing a version, otherwise use current site work items
        const workItemsToUse = isViewingVersion && versionWorkItems ? versionWorkItems : site.workItems;
        if (workItemsToUse && workItemsToUse.length > 0) {
            mergeWorkItems(workItemsToUse);
        }
    }, [site.workItems, versionWorkItems, isViewingVersion, mergeWorkItems]);
    const handleSiteInfoChange = (field, value) => {
        setSiteInfo((prev) => ({ ...prev, [field]: value }));
    };
    // Handle area info field changes
    const handleAreaInfoChange = (index, value) => {
        const updated = [...areaInfo];
        updated[index] = { ...updated[index], value };
        setAreaInfo(updated);
    };
    // Fetch contact persons from contacts filtered by selected company
    const fetchContactPersons = useCallback(async () => {
        // Only fetch contacts if a company is selected
        if (!selectedCompanyName) {
            setContactPersons([]);
            return;
        }
        try {
            setLoadingContactPersons(true);
            // Avoid using email as search - API may not match by email, overwriting the contact list
            const searchParam = contactPersonSearchTerm?.includes("@")
                ? undefined
                : contactPersonSearchTerm || undefined;
            const response = await contactsService.getContacts({
                companyName: selectedCompanyName,
                search: searchParam,
                page: 1,
            });
            let allContacts = response.data.data || [];
            // Ensure pre-filled contact is in the list when dropdown opens
            if (site.contactPerson) {
                const prefilledId = site.contactPerson.id ?? site.contactPerson.contactId;
                const hasPrefilled = allContacts.some((c) => (c.id ?? c.contactId) === prefilledId);
                if (!hasPrefilled) {
                    allContacts = [site.contactPerson, ...allContacts];
                }
            }
            setContactPersons(allContacts);
        }
        catch (err) {
            console.error("Error fetching contact persons:", err);
            setContactPersons([]);
        }
        finally {
            setLoadingContactPersons(false);
        }
    }, [selectedCompanyName, contactPersonSearchTerm, site]);
    useEffect(() => {
        if (showContactPersonDropdown) {
            fetchContactPersons();
        }
    }, [showContactPersonDropdown, fetchContactPersons]);
    // Fetch contacts when company is selected (for initial load or when company changes)
    useEffect(() => {
        if (selectedCompanyName) {
            // Fetch contacts for the selected company (without search term filter for initial load)
            const loadCompanyContacts = async () => {
                try {
                    setLoadingContactPersons(true);
                    const contactResponse = await contactsService.getContacts({
                        companyName: selectedCompanyName,
                        page: 1,
                    });
                    let contacts = contactResponse.data.data || [];
                    // Ensure pre-filled contact is in the list (e.g. when not on first page)
                    if (site.contactPerson) {
                        const prefilledId = site.contactPerson.id ??
                            site.contactPerson.contactId;
                        const hasPrefilled = contacts.some((c) => (c.id ?? c.contactId) === prefilledId);
                        if (!hasPrefilled) {
                            contacts = [site.contactPerson, ...contacts];
                        }
                    }
                    setContactPersons(contacts);
                }
                catch (err) {
                    console.error("Error fetching company contacts:", err);
                    setContactPersons([]);
                }
                finally {
                    setLoadingContactPersons(false);
                }
            };
            loadCompanyContacts();
        }
        else {
            // Clear contacts when no company is selected
            setContactPersons([]);
        }
    }, [selectedCompanyName, site]);
    useEffect(() => {
        if (!showContactPersonDropdown)
            return;
        const container = contactPersonDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && target && !container.contains(target)) {
                setShowContactPersonDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowContactPersonDropdown(false);
            }
        };
        const handleBlurClose = (event) => {
            const customEvent = event;
            if (customEvent.detail?.container === container) {
                setShowContactPersonDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        container.addEventListener("dropdown-blur-close", handleBlurClose);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
            container.removeEventListener("dropdown-blur-close", handleBlurClose);
        };
    }, [showContactPersonDropdown]);
    // Fetch unique company names from GHL
    const fetchCompanyNames = useCallback(async () => {
        try {
            setLoadingCompanyNames(true);
            const response = await contactsService.getUniqueCompanyNames();
            const allCompanyNames = response.data.data || [];
            // Filter company names based on search term
            let filteredNames = allCompanyNames;
            if (companySearchTerm && companySearchTerm.trim()) {
                const searchLower = companySearchTerm.toLowerCase().trim();
                filteredNames = allCompanyNames.filter((name) => name.toLowerCase().includes(searchLower));
            }
            setCompanyNames(filteredNames);
        }
        catch (err) {
            console.error("Error fetching company names:", err);
            setCompanyNames([]);
        }
        finally {
            setLoadingCompanyNames(false);
        }
    }, [companySearchTerm]);
    useEffect(() => {
        if (showCompanyDropdown) {
            fetchCompanyNames();
        }
    }, [showCompanyDropdown, fetchCompanyNames]);
    useEffect(() => {
        if (!showCompanyDropdown)
            return;
        const container = contactDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && target && !container.contains(target)) {
                setShowCompanyDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowCompanyDropdown(false);
            }
        };
        const handleBlurClose = (event) => {
            const customEvent = event;
            if (customEvent.detail?.container === container) {
                setShowCompanyDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        container.addEventListener("dropdown-blur-close", handleBlurClose);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
            container.removeEventListener("dropdown-blur-close", handleBlurClose);
        };
    }, [showCompanyDropdown]);
    const handleContactPersonSelect = (contactId, name) => {
        setSelectedContactPersonId(contactId);
        setSelectedContactPersonName(name);
        setContactPersonSearchTerm(name);
        setShowContactPersonDropdown(false);
    };
    const handleClearContactPerson = () => {
        setSelectedContactPersonId("");
        setSelectedContactPersonName("");
        setContactPersonSearchTerm("");
    };
    const handleCompanySelect = useCallback(async (companyName) => {
        setSelectedCompanyName(companyName);
        setCompanySearchTerm(companyName);
        setShowCompanyDropdown(false);
        // Clear contact person when company changes
        // This ensures Contact Person dropdown only shows contacts for the newly selected company
        setSelectedContactPersonId("");
        setSelectedContactPersonName("");
        setContactPersonSearchTerm("");
        // Fetch company contacts for the contact person dropdown using companyName filter
        // This API call filters contacts by company name - only contacts belonging to this company are returned
        try {
            const contactResponse = await contactsService.getContacts({
                companyName: companyName,
                page: 1,
            });
            const contacts = contactResponse.data.data || [];
            setContactPersons(contacts);
        }
        catch (err) {
            console.error("Error fetching company contacts:", err);
            setContactPersons([]);
        }
    }, []);
    const handleClearContact = () => {
        setSelectedCompanyName("");
        setCompanySearchTerm("");
        // Also clear contact person when company is cleared
        // This ensures Contact Person dropdown only shows contacts for the selected company
        setSelectedContactPersonId("");
        setSelectedContactPersonName("");
        setContactPersonSearchTerm("");
        setContactPersons([]);
    };
    // Suburb handlers
    const handleSuburbSelect = (suburb) => {
        setSuburbSearchTerm(suburb.name);
        handleSiteInfoChange("suburb", suburb.name);
        handleSiteInfoChange("state", suburb.state);
        handleSiteInfoChange("postcode", suburb.postcode);
        setShowSuburbDropdown(false);
    };
    const handleClearSuburb = () => {
        setSuburbSearchTerm("");
        handleSiteInfoChange("suburb", "");
        setFilteredSuburbs([]);
    };
    // Filter suburbs based on search term (fallback to Matthew Proctor if not in local list)
    useEffect(() => {
        if (suburbSearchTerm.trim()) {
            searchSuburbsAsync(suburbSearchTerm).then((results) => {
                setFilteredSuburbs(results.slice(0, 50));
            });
        }
        else {
            setFilteredSuburbs([]);
        }
    }, [suburbSearchTerm]);
    // Initialize suburb search term from site info
    useEffect(() => {
        if (siteInfo.suburb) {
            setSuburbSearchTerm(siteInfo.suburb);
        }
    }, [siteInfo.suburb]);
    // Handle outside clicks for suburb dropdown
    useEffect(() => {
        if (!showSuburbDropdown)
            return;
        const container = suburbDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && !container.contains(target)) {
                setShowSuburbDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowSuburbDropdown(false);
            }
        };
        const handleBlurClose = () => {
            setShowSuburbDropdown(false);
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        container.addEventListener("dropdown-blur-close", handleBlurClose);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
            container.removeEventListener("dropdown-blur-close", handleBlurClose);
        };
    }, [showSuburbDropdown]);
    const handleSave = useCallback(async () => {
        let currentSiteId = site.id;
        const isNewSite = !currentSiteId;
        try {
            if (!currentSiteId) {
                const createData = {
                    siteName: siteInfo.siteName || "New Site",
                    tenancyNumber: siteInfo.tenancyNumber || undefined,
                    tenancyName: siteInfo.tenancyName || undefined,
                    landlord: siteInfo.landlord || undefined,
                    address: siteInfo.address || undefined,
                    suburb: siteInfo.suburb || undefined,
                    state: siteInfo.state || undefined,
                    postcode: siteInfo.postcode || undefined,
                    buildingTypes: siteInfo.buildingType
                        ? [siteInfo.buildingType]
                        : undefined,
                    contactPersonId: selectedContactPersonId || undefined,
                    companyName: selectedCompanyName || undefined,
                    shoppingCentreId: shoppingCentreId.trim()
                        ? shoppingCentreId.trim()
                        : undefined,
                };
                const createResponse = await sitesService.createSite(createData);
                currentSiteId = createResponse.data.id;
            }
            if (!currentSiteId) {
                throw new Error("Failed to determine site id");
            }
            // Save site info
            const siteUpdateData = {
                siteName: siteInfo.siteName,
                tenancyNumber: siteInfo.tenancyNumber || undefined,
                tenancyName: siteInfo.tenancyName || undefined,
                landlord: siteInfo.landlord || undefined,
                address: siteInfo.address || undefined,
                suburb: siteInfo.suburb || undefined,
                state: siteInfo.state || undefined,
                postcode: siteInfo.postcode,
                // Convert single building type to array for API compatibility
                buildingTypes: siteInfo.buildingType
                    ? [siteInfo.buildingType]
                    : undefined,
                // Save contact person if changed (use same ID resolution as init/dropdown)
                contactPersonId: selectedContactPersonId !==
                    (site.contactPerson?.id ??
                        site.contactPerson?.contactId ??
                        "")
                    ? selectedContactPersonId || undefined
                    : undefined,
                // Save company name if changed
                companyName: selectedCompanyName !== (site.companyName || "")
                    ? selectedCompanyName || undefined
                    : undefined,
                // Save area information
                areaInfo: areaInfo,
                shoppingCentreId: shoppingCentreId.trim() || "",
            };
            await sitesService.updateSite(currentSiteId, siteUpdateData);
            // Collect all work items from accordion data
            const workItems = [];
            let sortOrder = 0;
            Object.entries(accordionData).forEach(([category, items]) => {
                if (category === "summary")
                    return;
                items.forEach((item) => {
                    item.sections?.forEach((section) => {
                        section.items.forEach((subItem) => {
                            // Determine quantity based on sectionId
                            let quantity = undefined;
                            if (section.sectionId === "scissor-lift" && subItem.weeksQty) {
                                quantity = parseFloat(subItem.weeksQty);
                            }
                            else if (section.sectionId === "skip-hire" &&
                                subItem.numberQty) {
                                quantity = parseFloat(subItem.numberQty);
                            }
                            else if (subItem.qty) {
                                quantity = parseFloat(subItem.qty);
                            }
                            workItems.push({
                                category,
                                sectionId: section.sectionId,
                                sectionTitle: section.sectionTitle,
                                itemId: subItem.id,
                                itemTitle: subItem.title,
                                inScope: subItem.inScope,
                                quantity: quantity,
                                unitCost: subItem.unitCost
                                    ? parseFloat(subItem.unitCost.replace("$", "").replace(",", ""))
                                    : undefined,
                                totalCost: subItem.totalCost
                                    ? parseFloat(subItem.totalCost.replace("$", "").replace(",", ""))
                                    : undefined,
                                notes: subItem.notes,
                                isDropdown: subItem.isDropdown,
                                dropdownOptions: subItem.dropdownOptions,
                                selectedOption: subItem.selectedOption,
                                sortOrder: sortOrder++,
                            });
                        });
                    });
                });
            });
            await sitesService.updateWorkItems(currentSiteId, workItems);
            onSave?.();
            if (isNewSite) {
                onSiteCreated?.(currentSiteId);
            }
            showSuccessToast("Site saved successfully!");
            return currentSiteId;
        }
        catch (err) {
            console.error("Error saving site:", err);
            throw err; // Re-throw so parent can handle
        }
    }, [
        site,
        siteInfo,
        accordionData,
        onSave,
        areaInfo,
        selectedContactPersonId,
        selectedCompanyName,
        onSiteCreated,
        showSuccessToast,
        showErrorToast,
        shoppingCentreId,
    ]);
    // Expose save handler via ref for parent component
    useEffect(() => {
        if (saveHandlerRef) {
            saveHandlerRef.current = handleSave;
        }
    }, [handleSave, saveHandlerRef]);
    // Notify parent when summary tab becomes active
    useEffect(() => {
        onSummaryTabChange?.(activeWorkCategory === "summary");
    }, [activeWorkCategory, onSummaryTabChange]);
    const renderSiteInfoContent = () => (_jsxs("div", { className: styles.formContainer, children: [_jsxs("div", { className: styles.paper, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Site" }), _jsx("input", { type: "text", className: "form-input", value: siteInfo.siteName, onChange: (e) => handleSiteInfoChange("siteName", e.target.value), readOnly: isViewingVersion })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Tenancy Number" }), _jsx("input", { type: "text", className: "form-input", value: siteInfo.tenancyNumber, onChange: (e) => handleSiteInfoChange("tenancyNumber", e.target.value), readOnly: isViewingVersion })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Tenancy Name" }), _jsx("input", { type: "text", className: "form-input", value: siteInfo.tenancyName, onChange: (e) => handleSiteInfoChange("tenancyName", e.target.value), readOnly: isViewingVersion })] }), _jsx("div", { className: styles.spacer }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Building Type" }), _jsx("select", { className: "form-select", value: siteInfo.buildingType, onChange: (e) => handleSiteInfoChange("buildingType", e.target.value), disabled: isViewingVersion, children: BUILDING_TYPE_OPTIONS.map((option) => (_jsx("option", { value: option.value, children: option.label }, option.value))) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Landlord" }), _jsx("input", { type: "text", className: "form-input", value: siteInfo.landlord, onChange: (e) => handleSiteInfoChange("landlord", e.target.value), readOnly: isViewingVersion })] }), _jsx("div", { className: styles.spacer }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Company" }), _jsxs("div", { ref: contactDropdownRef, className: styles.contactDropdownWrapper, "data-dropdown-container": true, children: [_jsxs("div", { className: styles.contactInputWrapper, children: [_jsx("input", { type: "text", className: styles.input, value: companySearchTerm, onChange: (e) => {
                                                    const newValue = e.target.value;
                                                    setCompanySearchTerm(newValue);
                                                    setShowCompanyDropdown(true);
                                                    // Allow clearing the field
                                                    if (!newValue) {
                                                        setSelectedCompanyName("");
                                                    }
                                                }, onFocus: () => setShowCompanyDropdown(true), onBlur: () => {
                                                    // On blur, reset search to selected company name if nothing new was selected
                                                    // Small delay to allow dropdown click to register
                                                    setTimeout(() => {
                                                        if (companySearchTerm !== selectedCompanyName) {
                                                            setCompanySearchTerm(selectedCompanyName);
                                                        }
                                                    }, 200);
                                                }, placeholder: "Type here to search" }), selectedCompanyName && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClearContact, children: "\u00D7" }))] }), showCompanyDropdown && (_jsx("div", { className: styles.contactDropdownMenu, children: loadingCompanyNames ? (_jsx("div", { className: styles.dropdownLoading, children: "Loading company names..." })) : companyNames.length === 0 ? (_jsx("div", { className: styles.dropdownEmpty, children: companySearchTerm
                                                ? "No matching companies in GHL. Please select from the list."
                                                : "Type to search companies from GHL" })) : (companyNames.map((companyName) => (_jsx("button", { type: "button", className: `${styles.contactOption} ${selectedCompanyName === companyName
                                                ? styles.selected
                                                : ""}`, onMouseDown: (e) => {
                                                e.preventDefault();
                                                handleCompanySelect(companyName);
                                            }, children: _jsx("span", { className: styles.contactName, children: companyName }) }, companyName)))) }))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Contact Person" }), _jsxs("div", { ref: contactPersonDropdownRef, className: styles.contactDropdownWrapper, "data-dropdown-container": true, children: [_jsxs("div", { className: styles.contactInputWrapper, children: [_jsx("input", { type: "text", className: styles.input, value: contactPersonSearchTerm, onChange: (e) => {
                                                    const newValue = e.target.value;
                                                    setContactPersonSearchTerm(newValue);
                                                    setShowContactPersonDropdown(true);
                                                    // Allow clearing the field
                                                    if (!newValue) {
                                                        setSelectedContactPersonId("");
                                                        setSelectedContactPersonName("");
                                                    }
                                                }, onFocus: () => setShowContactPersonDropdown(true), onBlur: () => {
                                                    // On blur, reset search to selected contact person name if nothing new was selected
                                                    // Small delay to allow dropdown click to register
                                                    setTimeout(() => {
                                                        if (contactPersonSearchTerm !== selectedContactPersonName) {
                                                            setContactPersonSearchTerm(selectedContactPersonName);
                                                        }
                                                    }, 200);
                                                }, placeholder: selectedCompanyName
                                                    ? "Type here to search"
                                                    : "Select a company first", readOnly: isViewingVersion, disabled: !selectedCompanyName && !isViewingVersion }), selectedContactPersonName && !isViewingVersion && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClearContactPerson, children: "\u00D7" }))] }), showContactPersonDropdown && !isViewingVersion && (_jsx("div", { className: styles.contactDropdownMenu, children: !selectedCompanyName ? (_jsx("div", { className: styles.dropdownEmpty, children: "Please select a company first" })) : loadingContactPersons ? (_jsx("div", { className: styles.dropdownLoading, children: "Loading contact persons..." })) : contactPersons.length === 0 ? (_jsx("div", { className: styles.dropdownEmpty, children: contactPersonSearchTerm
                                                ? "No matching contacts found. Please select from the list."
                                                : "Type to search contacts" })) : (contactPersons.map((contact) => {
                                            const displayName = getContactDisplayName(contact.firstName, contact.lastName, contact.email);
                                            const contactIdForApi = contact.id ?? contact.contactId;
                                            return (_jsx("button", { type: "button", className: `${styles.contactOption} ${selectedContactPersonId === contactIdForApi
                                                    ? styles.selected
                                                    : ""}`, onMouseDown: (e) => {
                                                    e.preventDefault();
                                                    handleContactPersonSelect(contactIdForApi, displayName);
                                                }, children: _jsx("span", { className: styles.contactName, children: displayName }) }, contactIdForApi));
                                        })) }))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Shopping centre" }), _jsx("div", { className: styles.shoppingCentreField, children: _jsx(SearchableShoppingCentreSelect, { value: shoppingCentreId, onChange: setShoppingCentreId, disabled: isViewingVersion }) })] })] }), _jsxs("div", { className: styles.paper, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Address" }), _jsx("input", { type: "text", className: "form-input", value: siteInfo.address, onChange: (e) => handleSiteInfoChange("address", e.target.value), readOnly: isViewingVersion })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Suburb" }), _jsxs("div", { ref: suburbDropdownRef, className: styles.contactDropdownWrapper, "data-dropdown-container": true, children: [_jsxs("div", { className: styles.contactInputWrapper, children: [_jsx("input", { type: "text", className: styles.input, value: suburbSearchTerm, onChange: (e) => {
                                                    const newValue = e.target.value;
                                                    setSuburbSearchTerm(newValue);
                                                    setShowSuburbDropdown(true);
                                                    // Update site info as user types
                                                    handleSiteInfoChange("suburb", newValue);
                                                }, onFocus: () => setShowSuburbDropdown(true), placeholder: "Type here to search", readOnly: isViewingVersion }), suburbSearchTerm && !isViewingVersion && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClearSuburb, children: "\u00D7" }))] }), showSuburbDropdown && !isViewingVersion && (_jsx("div", { className: styles.contactDropdownMenu, children: filteredSuburbs.length === 0 && suburbSearchTerm ? (_jsx("div", { className: styles.dropdownEmpty, children: "No suburbs found" })) : filteredSuburbs.length === 0 ? (_jsx("div", { className: styles.dropdownEmpty, children: "Start typing to search suburbs..." })) : (filteredSuburbs.map((suburb, index) => (_jsx("button", { type: "button", className: `${styles.contactOption} ${siteInfo.suburb === suburb.name ? styles.selected : ""}`, onClick: () => handleSuburbSelect(suburb), children: _jsxs("span", { className: styles.contactName, children: [suburb.name, ", ", suburb.state, " ", suburb.postcode] }) }, `${suburb.name}-${suburb.state}-${index}`)))) }))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "State" }), _jsx("input", { type: "text", className: "form-input", value: siteInfo.state, onChange: (e) => handleSiteInfoChange("state", e.target.value), readOnly: isViewingVersion })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Postcode" }), _jsx("input", { type: "text", className: "form-input", value: siteInfo.postcode, onChange: (e) => handleSiteInfoChange("postcode", e.target.value), readOnly: isViewingVersion })] }), _jsx("div", { className: styles.spacer }), _jsxs("div", { children: [_jsx("h3", { className: styles.sectionTitle, children: "AREA INFORMATION" }), _jsx("div", { className: styles.areaInfoGrid, children: areaInfo.map((item, index) => (_jsxs("div", { className: styles.areaInfoRow, children: [_jsx("label", { className: styles.areaLabel, children: item.label }), _jsx("input", { type: "number", className: styles.areaInput, value: item.value, onFocus: (e) => {
                                                if (item.value === 0)
                                                    e.target.select();
                                            }, onChange: (e) => handleAreaInfoChange(index, Number(e.target.value)), disabled: isViewingVersion })] }, index))) })] })] })] }));
    const toggleAccordion = (id) => {
        setExpandedAccordions((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            }
            else {
                newSet.add(id);
            }
            return newSet;
        });
    };
    const handleExpandAll = () => {
        const currentItems = accordionData[activeWorkCategory];
        const allIds = currentItems.map((item) => item.id);
        setExpandedAccordions(new Set(allIds));
    };
    const handleCollapseAll = () => {
        setExpandedAccordions(new Set());
    };
    const toggleSubItemScope = (itemId, sectionIndex, subItemId) => {
        setAccordionData((prevData) => {
            const newData = { ...prevData };
            const categoryItems = [...newData[activeWorkCategory]];
            const itemIndex = categoryItems.findIndex((item) => item.id === itemId);
            if (itemIndex === -1)
                return prevData;
            const item = { ...categoryItems[itemIndex] };
            if (!item.sections)
                return prevData;
            const sections = [...item.sections];
            const section = { ...sections[sectionIndex] };
            const items = [...section.items];
            const subItemIndex = items.findIndex((sub) => sub.id === subItemId);
            if (subItemIndex === -1)
                return prevData;
            const toggledItem = items[subItemIndex];
            const newInScope = !toggledItem.inScope;
            const isAfterHoursItem = toggledItem.title
                .toLowerCase()
                .includes("after hours");
            // Preserve edited unitCost for After Hours items when toggling
            // Only set default from Cost Settings if unitCost is empty
            let updatedUnitCost = toggledItem.unitCost;
            if (isAfterHoursItem) {
                if (newInScope) {
                    // Toggling to Yes - only set default if unitCost is empty
                    if (!toggledItem.unitCost || toggledItem.unitCost.trim() === "") {
                        // First try to use defaultUnitCost if available
                        if (toggledItem.defaultUnitCost != null) {
                            updatedUnitCost = `${toggledItem.defaultUnitCost}`;
                        }
                        // If not available, look up from costParameters
                        else {
                            const costParam = costParameters.find((cp) => cp.sectionId === section.sectionId &&
                                cp.itemTitle
                                    ?.toLowerCase()
                                    .includes("after hours works required"));
                            if (costParam?.unitCost != null) {
                                updatedUnitCost = `${costParam.unitCost}`;
                                // Also store it as defaultUnitCost for future use
                                toggledItem.defaultUnitCost = costParam.unitCost;
                            }
                        }
                    }
                    // If unitCost already has a value, preserve it (don't overwrite edited values)
                }
                else {
                    // Toggling to No - preserve the value (don't clear it)
                    // This allows admins to keep their edited rate even when toggled off
                    updatedUnitCost = toggledItem.unitCost || "";
                }
            }
            items[subItemIndex] = {
                ...toggledItem,
                inScope: newInScope,
                unitCost: updatedUnitCost,
            };
            // Calculate or clear total cost for the toggled item
            const isShopfrontOver36m = toggledItem.title
                .toLowerCase()
                .includes("over 3.6m");
            if (newInScope) {
                // For OVER 3.6m item, calculate even without unit cost
                if (isShopfrontOver36m && section.sectionId === "shopfronts") {
                    // Get section settings for calculation - check from current items array
                    const afterHoursItem = items.find((item) => item.title.toLowerCase().includes("after hours"));
                    const afterHoursEnabled = afterHoursItem?.inScope || false;
                    // Extract After Hours multiplier from the item's unitCost
                    const afterHoursMultiplier = afterHoursEnabled && afterHoursItem?.unitCost
                        ? parseFloat(afterHoursItem.unitCost.toString().replace(/[^0-9.-]/g, "")) || 1.3
                        : undefined;
                    const shopfrontSettings = getShopfrontSettingsFromItems(items, costParameters);
                    // Calculate shopfront items total for OVER 3.6m item
                    const shopfrontItemsTotal = getShopfrontItemsTotal(items, shopfrontSettings);
                    // Calculate total cost (unitCost can be 0 for OVER 3.6m item)
                    const calculationResult = calculateTotalCost({
                        itemId: subItemId,
                        sectionId: section.sectionId,
                        category: activeWorkCategory,
                        title: toggledItem.title,
                        unitCost: 0, // Not used for OVER 3.6m calculation
                        areaInfo,
                        afterHoursEnabled,
                        afterHoursMultiplier,
                        shopfrontItemsTotal,
                    });
                    items[subItemIndex].totalCost =
                        calculationResult.totalCost.toFixed(2);
                }
                else if (updatedUnitCost) {
                    // For other items, require unit cost
                    const unitCost = parseFloat(updatedUnitCost) || 0;
                    if (unitCost > 0) {
                        // Get section settings for calculation - check from current items array
                        const afterHoursItem = items.find((item) => item.title.toLowerCase().includes("after hours"));
                        const afterHoursEnabled = afterHoursItem?.inScope || false;
                        // Extract After Hours multiplier from the item's unitCost
                        const afterHoursMultiplier = afterHoursEnabled && afterHoursItem?.unitCost
                            ? parseFloat(afterHoursItem.unitCost.toString().replace(/[^0-9.-]/g, "")) || 1.3
                            : undefined;
                        const shopfrontSettings = getShopfrontSettingsFromItems(items, costParameters);
                        // Find Float value from Cost Settings for "19" Electric calculation
                        const isScissorLiftSection = section.sectionId === "scissor-lift";
                        const is19Electric = isScissorLiftSection &&
                            toggledItem.title.toLowerCase().includes("19") &&
                            toggledItem.title.toLowerCase().includes("electric");
                        const floatCostParam = is19Electric
                            ? costParameters.find((cp) => cp.category === "preliminary_cost" &&
                                cp.sectionId === "scissor-lift" &&
                                cp.itemTitle?.toLowerCase().includes("float"))
                            : null;
                        const floatValue = floatCostParam?.unitCost ?? undefined;
                        // Calculate base total cost (without height factor)
                        const calculationResult = calculateTotalCost({
                            itemId: subItemId,
                            sectionId: section.sectionId,
                            category: activeWorkCategory,
                            title: toggledItem.title,
                            unitCost: parseFloat(updatedUnitCost) || 0,
                            quantity: toggledItem.qty
                                ? parseFloat(toggledItem.qty)
                                : undefined,
                            weeksQty: toggledItem.weeksQty
                                ? parseFloat(toggledItem.weeksQty)
                                : undefined,
                            numberQty: toggledItem.numberQty
                                ? parseFloat(toggledItem.numberQty)
                                : undefined,
                            areaInfo,
                            afterHoursEnabled,
                            afterHoursMultiplier,
                            floatValue: is19Electric ? floatValue : undefined,
                        });
                        // Apply height factor for shopfront items if toggle is ON
                        let finalTotal = calculationResult.totalCost;
                        if (section.sectionId === "shopfronts" &&
                            shopfrontSettings.heightEnabled) {
                            const isStandardGlazed = subItemId === "shopfront-1" ||
                                toggledItem.title.toLowerCase().includes("standard glazed") ||
                                toggledItem.title.toLowerCase().includes("standard");
                            const isHeavyDuty = subItemId === "shopfront-2" ||
                                toggledItem.title.toLowerCase().includes("heavy duty") ||
                                toggledItem.title.toLowerCase().includes("heavy");
                            if (isStandardGlazed) {
                                finalTotal *= shopfrontSettings.stdHeightFactor;
                            }
                            else if (isHeavyDuty) {
                                finalTotal *= shopfrontSettings.hvyHeightFactor;
                            }
                        }
                        items[subItemIndex].totalCost = finalTotal.toFixed(2);
                    }
                }
            }
            else if (!newInScope) {
                // Clear total cost when not in scope
                items[subItemIndex].totalCost = "";
            }
            // Check if this is the shopfront OVER 3.6m toggle
            const isShopfrontOver36mToggle = subItemId === "shopfront-3a" ||
                toggledItem.title?.toLowerCase().includes("over 3.6m");
            // Handle shopfront-3a toggle: load shopfront-3 and shopfront-4 defaults when toggled YES
            if (isShopfrontOver36mToggle && section.sectionId === "shopfronts") {
                if (newInScope) {
                    // Toggling to YES - load shopfront-3 and shopfront-4 defaults from costsettings if not already stored
                    const shopfront3Item = items.find((item) => item.id === "shopfront-3");
                    const shopfront4Item = items.find((item) => item.id === "shopfront-4");
                    // Load shopfront-3 default if not already stored
                    if (shopfront3Item &&
                        (!shopfront3Item.unitCost || shopfront3Item.unitCost.trim() === "")) {
                        const shopfront3CostParam = costParameters.find((cp) => cp.itemId === "shopfront-3" ||
                            cp.itemTitle
                                ?.toLowerCase()
                                .includes("shopfront height factor std"));
                        if (shopfront3CostParam?.unitCost != null) {
                            const shopfront3Index = items.findIndex((item) => item.id === "shopfront-3");
                            if (shopfront3Index !== -1) {
                                items[shopfront3Index] = {
                                    ...items[shopfront3Index],
                                    unitCost: `${shopfront3CostParam.unitCost}`,
                                };
                            }
                        }
                    }
                    // Load shopfront-4 default if not already stored
                    if (shopfront4Item &&
                        (!shopfront4Item.unitCost || shopfront4Item.unitCost.trim() === "")) {
                        const shopfront4CostParam = costParameters.find((cp) => cp.itemId === "shopfront-4" ||
                            cp.itemTitle
                                ?.toLowerCase()
                                .includes("shopfront height factor hvy"));
                        if (shopfront4CostParam?.unitCost != null) {
                            const shopfront4Index = items.findIndex((item) => item.id === "shopfront-4");
                            if (shopfront4Index !== -1) {
                                items[shopfront4Index] = {
                                    ...items[shopfront4Index],
                                    unitCost: `${shopfront4CostParam.unitCost}`,
                                };
                            }
                        }
                    }
                }
            }
            // If toggling a factor item, recalculate all affected items in the section using two-pass approach
            const isFactorItem = toggledItem.title?.toLowerCase().includes("after hours") ||
                toggledItem.title?.includes("Height Factor") ||
                isShopfrontOver36mToggle;
            // Always recalculate when factor/height toggle changes to ensure dependent items
            // (e.g. shopfront-1 and shopfront-2) are recalculated with the correct factors
            if (isFactorItem || isShopfrontOver36mToggle) {
                // Use recalculateSectionItems helper for proper two-pass calculation
                const recalculatedItems = recalculateSectionItems(items, section.sectionId, activeWorkCategory, costParameters, areaInfo);
                // Update items array with recalculated values
                recalculatedItems.forEach((recalculatedItem, index) => {
                    items[index] = recalculatedItem;
                });
                const isScissorLiftToggle = subItemId === "scissor-1";
                // If the scissor-lift toggle changed, also recalculate the scissor-lift section
                // so that the \"19\" Electric item is always up to date (including float value)
                if (isScissorLiftToggle && section.sectionId === "scissor-lift") {
                    const scissorLiftItemIndex = categoryItems.findIndex((catItem) => catItem.sections?.some((s) => s.sectionId === "scissor-lift"));
                    if (scissorLiftItemIndex !== -1) {
                        const scissorLiftItem = { ...categoryItems[scissorLiftItemIndex] };
                        if (scissorLiftItem.sections) {
                            const scissorLiftSections = [...scissorLiftItem.sections];
                            const scissorLiftSectionIndex = scissorLiftSections.findIndex((s) => s.sectionId === "scissor-lift");
                            if (scissorLiftSectionIndex !== -1) {
                                const scissorLiftSection = {
                                    ...scissorLiftSections[scissorLiftSectionIndex],
                                };
                                const scissorLiftItems = [...scissorLiftSection.items];
                                const recalculatedScissorLiftItems = recalculateSectionItems(scissorLiftItems, "scissor-lift", activeWorkCategory, costParameters, areaInfo);
                                scissorLiftSection.items = recalculatedScissorLiftItems;
                                scissorLiftSections[scissorLiftSectionIndex] =
                                    scissorLiftSection;
                                scissorLiftItem.sections = scissorLiftSections;
                                categoryItems[scissorLiftItemIndex] = scissorLiftItem;
                            }
                        }
                    }
                }
                // If the OVER 3.6m shopfront toggle changed, also recalculate the scissor-lift section
                // so that the \"19\" Electric item is always up to date (including float value)
                if (isShopfrontOver36mToggle && section.sectionId === "shopfronts") {
                    const scissorLiftItemIndex = categoryItems.findIndex((catItem) => catItem.sections?.some((s) => s.sectionId === "scissor-lift"));
                    if (scissorLiftItemIndex !== -1) {
                        const scissorLiftItem = { ...categoryItems[scissorLiftItemIndex] };
                        if (scissorLiftItem.sections) {
                            const scissorLiftSections = [...scissorLiftItem.sections];
                            const scissorLiftSectionIndex = scissorLiftSections.findIndex((s) => s.sectionId === "scissor-lift");
                            if (scissorLiftSectionIndex !== -1) {
                                const scissorLiftSection = {
                                    ...scissorLiftSections[scissorLiftSectionIndex],
                                };
                                const scissorLiftItems = [...scissorLiftSection.items];
                                const recalculatedScissorLiftItems = recalculateSectionItems(scissorLiftItems, "scissor-lift", activeWorkCategory, costParameters, areaInfo);
                                scissorLiftSection.items = recalculatedScissorLiftItems;
                                scissorLiftSections[scissorLiftSectionIndex] =
                                    scissorLiftSection;
                                scissorLiftItem.sections = scissorLiftSections;
                                categoryItems[scissorLiftItemIndex] = scissorLiftItem;
                            }
                        }
                    }
                }
            }
            section.items = items;
            sections[sectionIndex] = section;
            item.sections = sections;
            categoryItems[itemIndex] = item;
            newData[activeWorkCategory] = categoryItems;
            return newData;
        });
    };
    const updateSubItemField = (itemId, sectionIndex, subItemId, field, value) => {
        setAccordionData((prevData) => {
            const newData = { ...prevData };
            const categoryItems = [...newData[activeWorkCategory]];
            const itemIndex = categoryItems.findIndex((item) => item.id === itemId);
            if (itemIndex === -1)
                return prevData;
            const item = { ...categoryItems[itemIndex] };
            if (!item.sections)
                return prevData;
            const sections = [...item.sections];
            const section = { ...sections[sectionIndex] };
            const items = [...section.items];
            const subItemIndex = items.findIndex((sub) => sub.id === subItemId);
            if (subItemIndex === -1)
                return prevData;
            // Update the field value
            const updatedSubItem = { ...items[subItemIndex], [field]: value };
            items[subItemIndex] = updatedSubItem;
            // If updating shopfront-3 or shopfront-4 unitCost, trigger recalculation of dependent items
            const isShopfront3Or4 = subItemId === "shopfront-3" || subItemId === "shopfront-4";
            if (isShopfront3Or4 &&
                field === "unitCost" &&
                section.sectionId === "shopfronts") {
                // Recalculate shopfront-1 and shopfront-2 with new height factors
                // The items array already has the updated shopfront-3/4 value
                const recalculatedItems = recalculateSectionItems(items, section.sectionId, activeWorkCategory, costParameters, areaInfo);
                // Replace items with recalculated items, but preserve our updated shopfront-3/4 value
                recalculatedItems.forEach((recalculatedItem, index) => {
                    if (recalculatedItem.id === subItemId) {
                        // Preserve the updated value we just set (don't let recalculation overwrite it)
                        items[index] = updatedSubItem;
                    }
                    else {
                        items[index] = recalculatedItem;
                    }
                });
                section.items = items;
                sections[sectionIndex] = section;
                item.sections = sections;
                // Also recalculate scissor-lift section if shopfront-3a is enabled
                const shopfront3aItem = items.find((item) => item.id === "shopfront-3a");
                if (shopfront3aItem?.inScope) {
                    const scissorLiftItemIndex = categoryItems.findIndex((catItem) => catItem.sections?.some((s) => s.sectionId === "scissor-lift"));
                    if (scissorLiftItemIndex !== -1) {
                        const scissorLiftItem = { ...categoryItems[scissorLiftItemIndex] };
                        if (scissorLiftItem.sections) {
                            const scissorLiftSections = [...scissorLiftItem.sections];
                            const scissorLiftSectionIndex = scissorLiftSections.findIndex((s) => s.sectionId === "scissor-lift");
                            if (scissorLiftSectionIndex !== -1) {
                                const scissorLiftSection = {
                                    ...scissorLiftSections[scissorLiftSectionIndex],
                                };
                                const scissorLiftItems = [...scissorLiftSection.items];
                                const recalculatedScissorLiftItems = recalculateSectionItems(scissorLiftItems, "scissor-lift", activeWorkCategory, costParameters, areaInfo);
                                scissorLiftSection.items = recalculatedScissorLiftItems;
                                scissorLiftSections[scissorLiftSectionIndex] =
                                    scissorLiftSection;
                                scissorLiftItem.sections = scissorLiftSections;
                                categoryItems[scissorLiftItemIndex] = scissorLiftItem;
                            }
                        }
                    }
                }
                categoryItems[itemIndex] = item;
                newData[activeWorkCategory] = categoryItems;
                return newData;
            }
            // Recalculate all items in the section using two-pass approach
            // This ensures proper handling of After Hours items and OVER 3.6m recalculation
            const recalculatedItems = recalculateSectionItems(items, section.sectionId, activeWorkCategory, costParameters, areaInfo);
            section.items = recalculatedItems;
            sections[sectionIndex] = section;
            item.sections = sections;
            categoryItems[itemIndex] = item;
            newData[activeWorkCategory] = categoryItems;
            return newData;
        });
    };
    const handleCreateEstimate = useCallback(async () => {
        if (!site.id)
            return;
        try {
            // Merge site's areaInfo with default template to ensure all items are present
            const areaInfo = mergeAreaInfo(site.areaInfo, DEFAULT_AREA_INFO);
            // Use site's builders margin settings if available, otherwise use current state values
            const marginEnabled = site.buildersMarginEnabled !== undefined
                ? site.buildersMarginEnabled
                : buildersMarginEnabled;
            const marginPercentage = site.buildersMarginPercentage !== undefined &&
                site.buildersMarginPercentage !== null
                ? site.buildersMarginPercentage
                : buildersMarginPercentage;
            const estimateData = {
                siteId: site.id,
                title: `${siteInfo.siteName} - Estimate`,
                buildersMarginEnabled: marginEnabled,
                buildersMarginPercentage: marginPercentage,
                areaInfo,
                ...(shoppingCentreId.trim()
                    ? { shoppingCentreId: shoppingCentreId.trim() }
                    : {}),
            };
            const response = await quotesService.createEstimateFromSite(estimateData);
            const newQuote = response.data;
            // Show success message
            showSuccessToast("Estimate created successfully!");
            // Navigate to the estimation edit page
            navigate(`/admin/estimating/${newQuote.id}/edit`);
        }
        catch (err) {
            console.error("Error creating estimate:", err);
            const errorMessage = err?.response?.data?.message ||
                err?.message ||
                "Failed to create estimate";
            showErrorToast(errorMessage);
        }
    }, [
        site.id,
        site.areaInfo,
        site.buildersMarginEnabled,
        site.buildersMarginPercentage,
        siteInfo.siteName,
        buildersMarginEnabled,
        buildersMarginPercentage,
        navigate,
        showSuccessToast,
        showErrorToast,
        shoppingCentreId,
    ]);
    // Expose create estimate handler via ref for parent component
    useEffect(() => {
        if (createEstimateHandlerRef) {
            createEstimateHandlerRef.current = handleCreateEstimate;
        }
    }, [handleCreateEstimate, createEstimateHandlerRef]);
    const renderAccordionContent = () => {
        const items = accordionData[activeWorkCategory];
        const isSummary = activeWorkCategory === "summary";
        if (isSummary) {
            return (_jsx("div", { className: styles.summaryPaper, children: _jsxs("div", { className: styles.summaryContainer, children: [_jsxs("div", { className: styles.summaryRow, children: [_jsx("div", { className: styles.summaryLabel, children: "Sub Total" }), _jsx("div", { className: styles.summaryValue, children: calculatedSummary.subTotal })] }), _jsxs("div", { className: styles.summaryMarginRow, children: [_jsxs("div", { className: styles.summaryMarginLeft, children: [_jsx("span", { className: styles.summaryMarginLabel, children: "Builders Preliminaries & Margin" }), _jsx("button", { className: `${styles.toggleButton} ${buildersMarginEnabled ? styles.yes : styles.no}`, onClick: () => setBuildersMarginEnabled(!buildersMarginEnabled), children: buildersMarginEnabled ? "YES" : "NO" })] }), _jsxs("div", { className: styles.summaryMarginRight, children: [_jsxs("span", { className: styles.summaryMarginPercent, children: [buildersMarginPercentage, "%"] }), _jsx("span", { className: styles.summaryMarginAmount, children: calculatedSummary.buildersMarginAmount })] })] }), _jsxs("div", { className: styles.summaryTotalsSection, children: [_jsxs("div", { className: styles.summaryTotalRow, children: [_jsx("div", { className: styles.summaryTotalLabel, children: "Total" }), _jsx("div", { className: styles.summaryTotalValue, children: calculatedSummary.total })] }), _jsxs("div", { className: styles.summaryTotalRow, children: [_jsx("div", { className: styles.summaryTotalLabel, children: "Cost per m2" }), _jsx("div", { className: styles.summaryTotalValue, children: calculatedSummary.costPerM2 })] })] })] }) }));
        }
        return (_jsx("div", { className: styles.accordionList, children: items.map((item) => (_jsxs("div", { className: styles.accordionItem, children: [_jsxs("button", { className: `${styles.accordionHeader} ${expandedAccordions.has(item.id) ? styles.expanded : ""}`, onClick: () => toggleAccordion(item.id), children: [_jsxs("div", { className: styles.accordionTitleSection, children: [_jsx("svg", { className: styles.accordionChevron, width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M6 9l6 6 6-6" }) }), _jsx("span", { className: styles.accordionTitle, children: item.title })] }), item.total && (_jsx("span", { className: styles.accordionTotal, children: item.total }))] }), expandedAccordions.has(item.id) && (_jsx("div", { className: styles.accordionBody, children: item.sections ? (_jsx("div", { className: styles.subItemsTable, children: item.sections.map((section, sectionIndex) => (_jsxs("div", { className: styles.sectionBlock, children: [section.sectionTitle && (_jsx("div", { className: styles.sectionTitleRow, children: _jsx("span", { className: styles.sectionTitleText, children: section.sectionTitle }) })), section.isDropdownSection ? (_jsxs("div", { className: styles.tableHeaderDropdown, children: [_jsx("div", { className: styles.tableHeaderCell }), _jsx("div", { className: styles.tableHeaderCell, children: "Total Cost" }), _jsx("div", { className: styles.tableHeaderCell, children: "Notes" })] })) : (_jsxs("div", { className: section.showQty ||
                                            section.showWeeksQty ||
                                            section.showNumberQty
                                            ? styles.tableHeaderWithQty
                                            : styles.tableHeader, children: [_jsx("div", { className: styles.tableHeaderCell }), _jsx("div", { className: styles.tableHeaderCell, children: "In Scope" }), section.showQty && (_jsx("div", { className: styles.tableHeaderCell, children: "Qty" })), section.showWeeksQty && (_jsx("div", { className: styles.tableHeaderCell, children: "# of Weeks" })), section.showNumberQty && (_jsx("div", { className: styles.tableHeaderCell, children: "No." })), _jsx("div", { className: styles.tableHeaderCell, children: "Unit Cost" }), _jsx("div", { className: styles.tableHeaderCell, children: "Total Cost" }), _jsx("div", { className: styles.tableHeaderCell, children: "Notes" })] })), section.items
                                        .filter((subItem) => {
                                        // Filter out shopfront-3 and shopfront-4 - they don't have rows
                                        if (subItem.id === "shopfront-3" ||
                                            subItem.id === "shopfront-4") {
                                            return false;
                                        }
                                        const title = subItem.title.toLowerCase();
                                        return (!title.includes("shopfront height factor std") &&
                                            !title.includes("shopfront height factor hvy"));
                                    })
                                        .map((subItem) => section.isDropdownSection ? (_jsxs("div", { className: styles.tableRowDropdown, children: [_jsx("div", { className: styles.tableCellDropdown, children: _jsxs("div", { className: styles.dropdownFieldGroup, children: [_jsx("label", { className: styles.dropdownLabel, children: subItem.title }), _jsx("select", { className: styles.dropdownSelect, value: subItem.selectedOption || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "selectedOption", e.target.value), disabled: isViewingVersion, children: subItem.dropdownOptions?.map((option) => (_jsx("option", { value: option, children: option }, option))) })] }) }), _jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.costInput, value: subItem.totalCost || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "totalCost", e.target.value), readOnly: isViewingVersion || !canEditPrices }) }), _jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.notesInput, value: subItem.notes || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "notes", e.target.value), readOnly: isViewingVersion }) })] }, subItem.id)) : (_jsxs("div", { className: section.showQty ||
                                            section.showWeeksQty ||
                                            section.showNumberQty
                                            ? styles.tableRowWithQty
                                            : styles.tableRow, children: [_jsx("div", { className: styles.tableCell, children: _jsx("span", { className: styles.subItemTitle, children: subItem.title }) }), _jsx("div", { className: styles.tableCell, children: _jsx("button", { className: `${styles.toggleButton} ${subItem.inScope ? styles.yes : styles.no}`, onClick: () => toggleSubItemScope(item.id, sectionIndex, subItem.id), disabled: isViewingVersion, children: subItem.inScope ? "YES" : "NO" }) }), section.showQty && (_jsx("div", { className: styles.tableCell, children: !subItem.hideQty && (_jsx("input", { type: "text", className: styles.qtyInput, value: subItem.qty || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "qty", e.target.value), readOnly: isViewingVersion })) })), section.showWeeksQty && (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.qtyInput, value: subItem.weeksQty || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "weeksQty", e.target.value), readOnly: isViewingVersion }) })), section.showNumberQty && (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.qtyInput, value: subItem.numberQty || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "numberQty", e.target.value), readOnly: isViewingVersion }) })), (() => {
                                                const isShopfrontOver36m = subItem.id === "shopfront-3a" ||
                                                    subItem.title
                                                        .toLowerCase()
                                                        .includes("over 3.6m");
                                                const isAfterHours = subItem.title
                                                    .toLowerCase()
                                                    .includes("after hours");
                                                if (isShopfrontOver36m) {
                                                    // For shopfront-3a, display shopfront-3's unitCost in the unitCost field
                                                    // Get shopfront-3 item from the section
                                                    const shopfront3Item = section.items.find((item) => item.id === "shopfront-3");
                                                    const shopfront3Value = shopfront3Item?.unitCost || "";
                                                    // Only show input if toggle is YES
                                                    if (subItem.inScope) {
                                                        return (_jsxs("div", { className: `${styles.tableCell} ${styles.tableCellWithLabel}`, children: [_jsx("label", { children: shopfront3Item?.title }), _jsx("input", { type: "text", className: styles.costInput, value: shopfront3Value, onChange: (e) => {
                                                                        // Update shopfront-3's unitCost when user edits
                                                                        const shopfront3Index = section.items.findIndex((item) => item.id === "shopfront-3");
                                                                        if (shopfront3Index !== -1) {
                                                                            const normalizedValue = e.target.value
                                                                                .replace(/,/g, ".")
                                                                                .replace(/[^0-9.-]/g, "");
                                                                            const parts = normalizedValue.split(".");
                                                                            const finalValue = parts.length > 2
                                                                                ? parts[0] +
                                                                                    "." +
                                                                                    parts.slice(1).join("")
                                                                                : normalizedValue;
                                                                            updateSubItemField(item.id, sectionIndex, "shopfront-3", "unitCost", finalValue);
                                                                        }
                                                                    }, readOnly: isViewingVersion || !canEditPrices })] }));
                                                    }
                                                    else {
                                                        return (_jsx("div", { className: styles.tableCell }));
                                                    }
                                                }
                                                const isDecimalItem = isAfterHours ||
                                                    subItem.title
                                                        .toLowerCase()
                                                        .includes("shopfront height factor std") ||
                                                    subItem.title
                                                        .toLowerCase()
                                                        .includes("shopfront height factor hvy");
                                                // For decimal items, preserve raw string value to allow decimal input
                                                // Only format when displaying a finalized number (not during active editing)
                                                const displayValue = isDecimalItem
                                                    ? subItem.unitCost || ""
                                                    : subItem.unitCost || "";
                                                return (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.costInput, value: displayValue, onChange: (e) => {
                                                            let normalizedValue = e.target.value;
                                                            if (isDecimalItem) {
                                                                // Allow decimal input - replace comma with period, but preserve periods
                                                                normalizedValue =
                                                                    normalizedValue.replace(/,/g, ".");
                                                                // Allow only numbers, one decimal point, and negative sign
                                                                normalizedValue =
                                                                    normalizedValue.replace(/[^0-9.-]/g, "");
                                                                // Ensure only one decimal point
                                                                const parts = normalizedValue.split(".");
                                                                if (parts.length > 2) {
                                                                    normalizedValue =
                                                                        parts[0] +
                                                                            "." +
                                                                            parts.slice(1).join("");
                                                                }
                                                            }
                                                            updateSubItemField(item.id, sectionIndex, subItem.id, "unitCost", normalizedValue);
                                                        }, readOnly: isViewingVersion || !canEditPrices }) }));
                                            })(), (() => {
                                                const isShopfrontOver36m = subItem.id === "shopfront-3a" ||
                                                    subItem.title
                                                        .toLowerCase()
                                                        .includes("over 3.6m");
                                                const isAfterHours = subItem.title
                                                    .toLowerCase()
                                                    .includes("after hours");
                                                // For shopfront-3a, display shopfront-4's unitCost in the totalCost field
                                                if (isShopfrontOver36m) {
                                                    const shopfront4Item = section.items.find((item) => item.id === "shopfront-4");
                                                    const shopfront4Value = shopfront4Item?.unitCost || "";
                                                    // Only show input if toggle is YES
                                                    if (subItem.inScope) {
                                                        return (_jsxs("div", { className: `${styles.tableCell} ${styles.tableCellWithLabel}`, children: [_jsx("label", { children: shopfront4Item?.title }), _jsx("input", { type: "text", className: styles.costInput, value: shopfront4Value, onChange: (e) => {
                                                                        // Update shopfront-4's unitCost when user edits
                                                                        const shopfront4Index = section.items.findIndex((item) => item.id === "shopfront-4");
                                                                        if (shopfront4Index !== -1) {
                                                                            const normalizedValue = e.target.value
                                                                                .replace(/,/g, ".")
                                                                                .replace(/[^0-9.-]/g, "");
                                                                            const parts = normalizedValue.split(".");
                                                                            const finalValue = parts.length > 2
                                                                                ? parts[0] +
                                                                                    "." +
                                                                                    parts.slice(1).join("")
                                                                                : normalizedValue;
                                                                            updateSubItemField(item.id, sectionIndex, "shopfront-4", "unitCost", finalValue);
                                                                        }
                                                                    }, readOnly: isViewingVersion || !canEditPrices })] }));
                                                    }
                                                    else {
                                                        return (_jsx("div", { className: styles.tableCell }));
                                                    }
                                                }
                                                if (isAfterHours) {
                                                    return (_jsx("div", { className: styles.tableCell }));
                                                }
                                                return (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.costInput, value: subItem.totalCost || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "totalCost", e.target.value), readOnly: isViewingVersion || !canEditPrices }) }));
                                            })(), (() => {
                                                const isAfterHours = subItem.title
                                                    .toLowerCase()
                                                    .includes("after hours");
                                                const isShopfrontOver36m = subItem.id === "shopfront-3a" ||
                                                    subItem.title
                                                        .toLowerCase()
                                                        .includes("over 3.6m");
                                                // Hide notes column for shopfront-3a and After Hours items
                                                if (isAfterHours || isShopfrontOver36m) {
                                                    return null;
                                                }
                                                return (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.notesInput, value: subItem.notes || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "notes", e.target.value), readOnly: isViewingVersion }) }));
                                            })()] }, subItem.id)))] }, sectionIndex))) })) : null }))] }, item.id))) }));
    };
    const renderWorkDetailsContent = () => (_jsxs("div", { className: styles.workDetailsContainer, children: [_jsx("div", { className: styles.categoryTabsWrapper, children: _jsx("div", { className: styles.categoryTabList, children: WORK_DETAILS_CATEGORY_TABS.map((tab) => (_jsx("button", { className: `${styles.categoryTab} ${activeWorkCategory === tab.id ? styles.active : ""}`, onClick: () => {
                            setActiveWorkCategory(tab.id);
                            setExpandedAccordions(new Set());
                        }, children: tab.label }, tab.id))) }) }), activeWorkCategory !== "summary" && (_jsxs("div", { className: styles.accordionControls, children: [_jsxs("button", { className: styles.controlBtn, onClick: handleExpandAll, children: [_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M7 13l5 5 5-5M7 6l5 5 5-5" }) }), "Expand All"] }), _jsxs("button", { className: styles.controlBtn, onClick: handleCollapseAll, children: [_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M17 11l-5-5-5 5M17 18l-5-5-5 5" }) }), "Collapse All"] }), !isViewingVersion && (_jsx("button", { className: styles.controlBtn, onClick: handleRefreshPrices, disabled: refreshingPrices, children: refreshingPrices ? "Refreshing..." : "Refresh Prices" }))] })), _jsx("div", { className: styles.accordionContainer, children: renderAccordionContent() })] }));
    return (_jsxs("div", { className: styles.container, children: [_jsx("div", { className: styles.section, children: renderSiteInfoContent() }), _jsxs("div", { className: styles.section, children: [_jsx("h3", { className: styles.sectionTitle, children: "Work Details" }), renderWorkDetailsContent()] })] }));
};
